// Shared range-bar component — matches the legacy CSS (.range-bar) so
// grid + wind + any future detail view share the same visual language.
// Renders: current price on top, gradient track, gold marker, bounds below.
import { fmt } from "@/lib/format";

interface RangeBarProps {
  symbol: string;
  lower: number;
  upper: number;
  /** Current marker position (usually live price). */
  current: number;
  /** Optional headline above the track; defaults to formatted current price. */
  label?: string;
}

export default function RangeBar({ symbol, lower, upper, current, label }: RangeBarProps) {
  const range = upper - lower;
  const pct =
    range > 0
      ? Math.max(0, Math.min(100, ((current - lower) / range) * 100))
      : 50;

  return (
    <div className="relative bg-surface border border-border rounded-lg px-4 pt-3 pb-2 mb-4">
      {/* Current price headline */}
      <div className="text-center text-warn text-[0.8rem] font-semibold mb-3">
        {label ?? fmt(current, symbol)}
      </div>

      {/* Track with full-width gradient + animated marker */}
      <div className="relative h-1.5 rounded-full mx-2" style={{ background: "#2a2a35" }}>
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(90deg, #22c55e 0%, #eab308 50%, #ef4444 100%)",
            opacity: 0.3,
          }}
        />
        <div
          className="absolute top-1/2 w-3 h-3 rounded-full bg-warn transition-[left] duration-500 ease-out"
          style={{
            left: `${pct}%`,
            transform: "translate(-50%, -50%)",
            boxShadow: "0 0 8px #eab308",
          }}
        />
      </div>

      {/* Lower / upper labels */}
      <div className="flex justify-between text-[0.7rem] text-muted mt-1 px-2">
        <span>{fmt(lower, symbol)}</span>
        <span>{fmt(upper, symbol)}</span>
      </div>
    </div>
  );
}
